import { modelBadge } from '../utils/modelHelpers';
import type { RegistryModel } from '../hooks/useModels';
import { ModelPickerList, groupByProvider, PROVIDER_COLORS, toRgba } from '../shared-ui/ModelPickerList';

/* ── Props ─────────────────────────────────────────────────────────── */

interface Props {
  open: boolean;
  currentModel: string | null;
  models: RegistryModel[];
  /** Switch the session's primary model in-place (same chat, no new session) */
  onSwitchModel: (modelId: string) => void;
  /** Start a brand-new chat with this model */
  onNewChatWithModel?: (modelId: string) => void;
  /** Inject [CONSULT_MODEL: provider | ] into InputBar for a one-turn consultation */
  onConsultModel: (provider: string) => void;
  onClose: () => void;
  /** Master Gemini switch from `GET /profile` features.gemini_enabled. When false
   *  (the protofac/valko profiles), the Google provider group is hidden from both
   *  the switch-model list and the consultation grid. Default true. */
  geminiEnabled?: boolean;
}

/* ── Component ─────────────────────────────────────────────────────── */

export function ModelControlSheet({
  open,
  currentModel,
  models,
  onSwitchModel,
  onNewChatWithModel,
  onConsultModel,
  onClose,
  geminiEnabled = true,
}: Props) {
  if (!open) return null;

  // Section 1 (the switch-model list) is delegated to the shared <ModelPickerList>.
  // We still compute `groups` here for section 2 (consult grid) + the new-chat button —
  // both derive from the SAME `groupByProvider(models)` filter, so behaviour is unchanged.
  const groups = groupByProvider(models).filter((g) => geminiEnabled || g.provider !== 'google');

  return (
    <div class="mcs-backdrop" onClick={onClose}>
      <div class="mcs-sheet" onClick={(e) => e.stopPropagation()}>

        {/* Drag handle */}
        <div class="mcs-handle-bar" />

        {/* Header */}
        <div class="mcs-header">
          <span class="mcs-title">AI Model</span>
          <button class="mcs-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Section 1: Switch primary model (shared ModelPickerList) */}
        <div class="mcs-section-label">SWITCH MODEL FOR THIS CHAT</div>

        <ModelPickerList
          models={models}
          selectedModelId={currentModel}
          geminiEnabled={geminiEnabled}
          onPick={(id) => { onSwitchModel(id); setTimeout(onClose, 250); }}
        />

        {/* New chat affordance */}
        {onNewChatWithModel && (
          <button
            class="mcs-new-chat-btn"
            onClick={() => { onNewChatWithModel(groups[0]?.models[0]?.modelId ?? ''); onClose(); }}
          >
            + New chat
          </button>
        )}

        {/* Section 2: One-turn consultation */}
        <div class="mcs-divider" />
        <div class="mcs-section-label">GET A SECOND OPINION THIS TURN</div>
        <div class="mcs-consult-grid">
          {groups.map((group) => {
            const pc = PROVIDER_COLORS[group.provider] ?? '#888';
            const firstBadge = modelBadge(group.models[0].modelId, false);
            return (
              <button
                key={group.provider}
                class="mcs-consult-btn"
                style={`--pc: ${pc}; --pc-bg: ${toRgba(pc, 0.09)}; --pc-border: ${toRgba(pc, 0.35)}; --pc-hover-bg: ${toRgba(pc, 0.16)}; --pc-hover-border: ${toRgba(pc, 0.55)}`}
                onClick={() => { onConsultModel(group.provider); onClose(); }}
                title={`Ask ${group.label} AI for a second opinion`}
              >
                <span
                  class="mcs-consult-icon"
                  style={`background: ${toRgba(pc, 0.22)}; color: ${pc}`}
                >
                  {firstBadge.label[0]}
                </span>
                <span class="mcs-consult-label">{group.label}</span>
              </button>
            );
          })}
        </div>

        <div class="mcs-safe-bottom" />
      </div>
    </div>
  );
}
